/**
 * 트랙 버전 액션 훅들
 *
 * Orval 생성 훅을 래핑하여 사용하기 쉬운 인터페이스 제공
 */

'use client';

import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateTrackVersionWithPlaylist,
  invalidateTrackWithPlaylist,
} from '@/lib/api/cacheUtils';
import {
  useTrackVersionSelect,
  useTrackVersionToggleIncluded,
  useTrackVersionExtend,
  useTrackVersionMove,
  useTrackVersionSplit,
} from '@/lib/api/orval';
import { useToast } from '@/lib/hooks/useToast';
import { toError } from '@/lib/utils/errorUtils';

// ============================================================================
// useSelectTrackVersion
// ============================================================================

export function useSelectTrackVersion(trackId: string, playlistId?: string) {
  const queryClient = useQueryClient();
  const mutation = useTrackVersionSelect({
    mutation: {
      onSuccess: () => {
        invalidateTrackVersionWithPlaylist(queryClient, trackId, playlistId);
      },
    },
  });

  return {
    ...mutation,
    mutateAsync: async (versionId: string) => {
      return mutation.mutateAsync({
        id: versionId,
        data: {} as never,
      });
    },
  };
}

// ============================================================================
// useToggleTrackVersionInclusion
// ============================================================================

export function useToggleTrackVersionInclusion(
  trackId: string,
  versionId: string,
  playlistId?: string
) {
  const queryClient = useQueryClient();
  const mutation = useTrackVersionToggleIncluded({
    mutation: {
      onSuccess: () => {
        invalidateTrackVersionWithPlaylist(queryClient, trackId, playlistId);
      },
    },
  });

  return {
    ...mutation,
    mutate: (_isIncluded?: boolean) => {
      mutation.mutate({
        id: versionId,
        data: {} as never,
      });
    },
    mutateAsync: async (_isIncluded?: boolean) => {
      return mutation.mutateAsync({
        id: versionId,
        data: {} as never,
      });
    },
  };
}

// ============================================================================
// useExtendTrackVersion
// ============================================================================

interface UseExtendTrackVersionOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useExtendTrackVersion(
  trackId: string,
  versionId: string,
  playlistId?: string,
  options?: UseExtendTrackVersionOptions
) {
  const queryClient = useQueryClient();
  const mutation = useTrackVersionExtend({
    mutation: {
      onSuccess: () => {
        invalidateTrackVersionWithPlaylist(queryClient, trackId, playlistId);
        options?.onSuccess?.();
      },
      onError: (error) => {
        options?.onError?.(toError(error));
      },
    },
  });

  return {
    ...mutation,
    mutateAsync: async (_data?: Record<string, unknown>) => {
      return mutation.mutateAsync({
        id: versionId,
        data: {} as never,
      });
    },
  };
}

// ============================================================================
// useMoveTrackVersion
// ============================================================================

export function useMoveTrackVersion(trackId: string, playlistId?: string) {
  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();

  const mutation = useTrackVersionMove({
    mutation: {
      onSuccess: () => {
        // 양쪽 트랙 캐시 무효화 (원본 트랙 + 전체 트랙 목록)
        if (playlistId) {
          invalidateTrackVersionWithPlaylist(queryClient, trackId, playlistId);
          invalidateTrackWithPlaylist(queryClient, playlistId);
        }
        success('버전이 이동되었습니다.');
      },
      onError: (err) => {
        toastError(toError(err).message || '버전 이동에 실패했습니다.');
      },
    },
  });

  return {
    ...mutation,
    mutateAsync: async (versionId: string, targetTrackId: string) => {
      return mutation.mutateAsync({
        id: versionId,
        data: { targetTrackId },
      });
    },
  };
}

// ============================================================================
// useSplitTrackVersion
// ============================================================================

export function useSplitTrackVersion(trackId: string, playlistId?: string) {
  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();

  const mutation = useTrackVersionSplit({
    mutation: {
      onSuccess: () => {
        // 원본 트랙 + 트랙 목록 + 플레이리스트 캐시 무효화
        if (playlistId) {
          invalidateTrackVersionWithPlaylist(queryClient, trackId, playlistId);
          invalidateTrackWithPlaylist(queryClient, playlistId);
        }
        success('버전이 새 트랙으로 분리되었습니다.');
      },
      onError: (err) => {
        toastError(toError(err).message || '버전 분리에 실패했습니다.');
      },
    },
  });

  return {
    ...mutation,
    mutateAsync: async (versionId: string, newTrackTitle?: string) => {
      return mutation.mutateAsync({
        id: versionId,
        data: { newTrackTitle: newTrackTitle || '' },
      });
    },
  };
}
